import {useTranslation} from 'react-i18next';
import {FieldRenderProps} from 'react-final-form';
import styled, {css} from 'styled-components';
import IconSelectArrow from '../assets/images/icon-select-arrow.svg';
import {RadioButtonStyled} from './RadioButtons';

interface FormInputProps extends FieldRenderProps<string> {
  sublabel?: string;
}

export const FormInput = ({input, label, meta, ...rest}: FormInputProps) => {
  const {t} = useTranslation();
  const error = meta.touched && (meta.error || meta.submitError);

  return (
    <InputWrapper>
      <FormInputLabel>{label}</FormInputLabel>
      <FormInputStyled {...input} {...rest} error={error} />
      {error && <FormError>{t(error)}</FormError>}
    </InputWrapper>
  );
};

export const FormTextarea = ({
  input,
  label,
  sublabel,
  meta,
  ...rest
}: FormInputProps) => {
  const {t} = useTranslation();
  const error = meta.touched && (meta.error || meta.submitError);

  return (
    <InputWrapper>
      <FormInputLabel>{label}</FormInputLabel>
      <FormTextareaStyled {...input} {...rest} error={error} />
      {sublabel && <FormSubLabel>{sublabel}</FormSubLabel>}
      {error && (
        <FormError>
          <pre>{t(error)}</pre>
        </FormError>
      )}
    </InputWrapper>
  );
};

export const FormCheckbox = ({input, meta, label, ...rest}: FormInputProps) => {
  const {t} = useTranslation();
  const error = meta.touched && (meta.error || meta.submitError);

  return (
    <>
      <CheckboxStyled>
        <label>
          <input type="checkbox" {...input} {...rest} />
          {label && <span dangerouslySetInnerHTML={{__html: label}}></span>}
        </label>
      </CheckboxStyled>
      {error && <FormError>{t(error)}</FormError>}
    </>
  );
};

export const FormRadioButton = ({
  input,
  meta,
  label,
  ...rest
}: FormInputProps) => {
  return (
    <>
      <RadioButtonStyled>
        <label>
          <input type="radio" {...input} {...rest} />
          {label && <span dangerouslySetInnerHTML={{__html: label}}></span>}
        </label>
      </RadioButtonStyled>
    </>
  );
};

export const FormSelect = ({
  input,
  meta,
  label,
  options,
  placeholder,
  ...rest
}: FormInputProps) => {
  const {t} = useTranslation();
  const error = meta.touched && (meta.error || meta.submitError);

  return (
    <InputWrapper>
      <FormInputLabel>{label}</FormInputLabel>
      <SelectContainer error={error}>
        <select {...input} {...rest}>
          <option defaultChecked value="" disabled>
            {placeholder || t('form.select-placeholder')}
          </option>
          {options.map(({label, value}: any, i: any) => (
            <option key={i} value={value}>
              {label}
            </option>
          ))}
        </select>
      </SelectContainer>
      {error && <FormError>{t(error)}</FormError>}
    </InputWrapper>
  );
};

export const SelectContainer = styled.div<{error?: boolean}>`
  position: relative;
  cursor: pointer;

  &::after {
    content: '';
    pointer-events: none;
    width: 16px;
    height: 10px;
    background-image: url(${IconSelectArrow});
    background-position: center;
    background-size: 100%;
    background-repeat: no-repeat;
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
  }

  select {
    cursor: pointer;
    appearance: none;
    display: block;
    width: 100%;
    padding: 20px 16px;
    border-radius: 8px;
    font-size: 16px;
    font-family: inherit;
    border: solid 1px ${(props) => props.theme.colors.gray300};
    background-color: ${(props) => props.theme.colors.gray100};
    color: ${(props) => props.theme.colors.dark800};
  }

  ${(p) =>
    p.error &&
    css`
      select {
        border-color: ${(props) => props.theme.colors.red};
      }
    `}
`;

export const FormTitle = styled.h3`
  font-size: 3rem;
  color: ${(props) => props.theme.colors.orange500};
  margin: 0;
  margin-bottom: 24px;

  @media ${(props) => props.theme.breakpoints.tablet} {
    font-size: 2rem;
    margin-bottom: 16px;
  }
`;

export const FormSubtitle = styled.p`
  margin: 0;
  margin-bottom: 32px;
`;

export const FormError = styled.p`
  color: ${(props) => props.theme.colors.red};
  font-weight: 700;
  font-size: 0.8rem;
  margin-bottom: 24px;
  margin-left: 10px;
  margin-top: 8px;
`;

export const FormInputLabel = styled.span`
  display: block;
  margin-left: 10px;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
`;

export const FormSubLabel = styled.small`
  display: block;
  margin: 0.5rem 0.5rem 0;
  font-size: 12px;
  font-weight: 500;
`;

const FormInputStyled = styled.input<{error: boolean}>`
  display: block;
  width: 100%;
  padding: 20px 16px;
  border-radius: 8px;
  border: solid 1px ${(props) => props.theme.colors.gray300};
  background-color: ${(props) => props.theme.colors.gray100};
  font-size: 16px;
  font-family: inherit;
  color: ${(props) => props.theme.colors.dark800};

  ${(p) =>
    p.error &&
    css`
      border-color: ${(props) => props.theme.colors.red};
    `}
`;

const FormTextareaStyled = styled.textarea<{error: boolean}>`
  display: block;
  width: 100%;
  padding: 20px 16px;
  border-radius: 8px;
  border: solid 1px ${(props) => props.theme.colors.gray300};
  background-color: ${(props) => props.theme.colors.gray100};
  font-size: 16px;
  font-family: inherit;
  color: ${(props) => props.theme.colors.dark800};
  resize: vertical;
  min-height: 120px;

  ${(p) =>
    p.error &&
    css`
      border-color: ${(props) => props.theme.colors.red};
    `}
`;

const CheckboxStyled = styled.div`
  label {
    position: relative;
    margin-bottom: 24px;
    display: inline-block;
    cursor: pointer;
  }

  span {
    position: relative;
    display: block;
    padding-left: 26px;
    font-size: 0.9rem;
    line-height: 1.3rem;
  }

  input {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    appearance: none;
  }

  input + span::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 18px;
    border-radius: 8px;
    border: solid 1px ${(props) => props.theme.colors.gray300};
    background-color: ${(props) => props.theme.colors.gray100};
  }

  & input:checked + span::before {
    content: '';
    position: absolute;
    top: 4px;
    left: 4px;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: ${(props) => props.theme.colors.orange500};
    z-index: 1;
  }
`;

const InputWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;

  pre {
    font-family: inherit;
  }
`;
